// File Sharing Service - Handles file share creation and management
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';
import { throwIfManuallySuspended } from '@utils/suspension';

export interface FileShare {
  id: string;
  file_id: string;
  user_id: string;
  share_token: string;
  password: string | null;
  expires_at: string | null;
  max_downloads: number | null;
  download_count: number;
  views: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contact_email_override: string | null;
  instagram_url_override: string | null;
  beatstars_profile_url_override: string | null;
  beatstars_sell_url: string | null;
  beatstars_price: string | null;
  show_buy_button: boolean;
  show_contact: boolean;
}

export interface CreateFileShareOptions {
  fileId: string;
  password?: string;
  expiresInDays?: number | null; // null = never expires
  maxDownloads?: number | null; // null = unlimited
  contactEmailOverride?: string | null;
  instagramUrlOverride?: string | null;
  beatstarsProfileUrlOverride?: string | null;
  beatstarsSellUrl?: string | null;
  beatstarsPrice?: string | null;
  showBuyButton?: boolean;
  showContact?: boolean;
}

export interface FileShareResult {
  share: FileShare;
  shareUrl: string;
}

class FileSharingService {
  /**
   * Create a new file share
   */
  async createFileShare(options: CreateFileShareOptions): Promise<FileShareResult> {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Check if account is manually suspended (without download access)
    await throwIfManuallySuspended();

    const { fileId, password, expiresInDays, maxDownloads, contactEmailOverride, instagramUrlOverride, beatstarsProfileUrlOverride, beatstarsSellUrl, beatstarsPrice, showBuyButton, showContact } = options;

    // Calculate expiration date
    let expiresAt: string | null = null;
    if (expiresInDays !== null && expiresInDays !== undefined) {
      const date = new Date();
      date.setDate(date.getDate() + expiresInDays);
      expiresAt = date.toISOString();
    }

    // Hash password if provided
    let hashedPassword: string | null = null;
    if (password && password.trim() !== '') {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    // Check if there's an existing active share for this file
    const { data: existingShare } = await supabase
      .from('file_shares')
      .select('*')
      .eq('file_id', fileId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    let share: FileShare;

    if (existingShare) {
      // Update existing share (payload includes new columns; if DB has no migration yet, we retry without them)
      const fullPayload = {
        password: hashedPassword,
        expires_at: expiresAt,
        max_downloads: maxDownloads,
        is_active: true,
        contact_email_override: contactEmailOverride?.trim() || null,
        instagram_url_override: instagramUrlOverride?.trim() || null,
        beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
        beatstars_sell_url: beatstarsSellUrl?.trim() || null,
        beatstars_price: beatstarsPrice?.trim() || null,
        show_buy_button: showBuyButton ?? true,
        show_contact: showContact ?? true,
      };
      const legacyPayload = {
        password: hashedPassword,
        expires_at: expiresAt,
        max_downloads: maxDownloads,
        is_active: true,
        contact_email_override: contactEmailOverride?.trim() || null,
        instagram_url_override: instagramUrlOverride?.trim() || null,
        beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
      };

      let result = await supabase
        .from('file_shares')
        .update(fullPayload)
        .eq('id', existingShare.id)
        .select()
        .single();

      if (result.error && result.error.message?.includes('schema cache') && /Could not find the .* column/.test(result.error.message)) {
        result = await supabase
          .from('file_shares')
          .update(legacyPayload)
          .eq('id', existingShare.id)
          .select()
          .single();
      }

      const { data: updatedShare, error: updateError } = result;

      if (updateError) {
        throw new Error(`Failed to update share: ${updateError.message}`);
      }

      share = updatedShare as FileShare;
    } else {
      // Generate unique token (with retry logic for uniqueness)
      let attempts = 0;
      let newShare: FileShare | null = null;
      const maxAttempts = 5;

      while (!newShare && attempts < maxAttempts) {
        const token = this.generateToken();

        const fullInsert = {
          file_id: fileId,
          user_id: user.id,
          share_token: token,
          password: hashedPassword,
          expires_at: expiresAt,
          max_downloads: maxDownloads,
          is_active: true,
          download_count: 0,
          views: 0,
          contact_email_override: contactEmailOverride?.trim() || null,
          instagram_url_override: instagramUrlOverride?.trim() || null,
          beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
          beatstars_sell_url: beatstarsSellUrl?.trim() || null,
          beatstars_price: beatstarsPrice?.trim() || null,
          show_buy_button: showBuyButton ?? true,
          show_contact: showContact ?? true,
        };
        const legacyInsert = {
          file_id: fileId,
          user_id: user.id,
          share_token: token,
          password: hashedPassword,
          expires_at: expiresAt,
          max_downloads: maxDownloads,
          is_active: true,
          download_count: 0,
          views: 0,
          contact_email_override: contactEmailOverride?.trim() || null,
          instagram_url_override: instagramUrlOverride?.trim() || null,
          beatstars_profile_url_override: beatstarsProfileUrlOverride?.trim() || null,
        };

        let createResult = await supabase
          .from('file_shares')
          .insert(fullInsert)
          .select()
          .single();

        if (createResult.error && createResult.error.message?.includes('schema cache') && /Could not find the .* column/.test(createResult.error.message)) {
          createResult = await supabase
            .from('file_shares')
            .insert(legacyInsert)
            .select()
            .single();
        }

        const { data, error: createError } = createResult;

        if (createError) {
          // If it's a unique constraint error, try again with a new token
          if (createError.code === '23505' && attempts < maxAttempts - 1) {
            attempts++;
            continue;
          }
          throw new Error(`Failed to create share: ${createError.message}`);
        }

        newShare = data as FileShare;
      }

      if (!newShare) {
        throw new Error('Failed to create share after multiple attempts');
      }

      share = newShare;
    }

    // Generate share URL - use /share/file/:token for single files
    const shareUrl = `${window.location.origin}/share/file/${share.share_token}`;

    return {
      share,
      shareUrl,
    };
  }

  /**
   * Get file share by file ID
   */
  async getFileShare(fileId: string): Promise<FileShare | null> {
    const user = useAuthStore.getState().user;
    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('file_shares')
      .select('*')
      .eq('file_id', fileId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    if (error || !data) {
      return null;
    }

    return data as FileShare;
  }

  /**
   * Deactivate a file share
   */
  async deactivateFileShare(fileId: string): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('Not authenticated');
    }

    // Check if account is manually suspended (without download access)
    await throwIfManuallySuspended();

    const { error } = await supabase
      .from('file_shares')
      .update({ is_active: false })
      .eq('file_id', fileId)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to deactivate share: ${error.message}`);
    }
  }

  /**
   * Generate a random token (fallback)
   */
  private generateToken(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').substring(0, 32);
  }
}

// Export singleton instance
export const fileSharingService = new FileSharingService();

